import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Switch } from './ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Progress } from './ui/progress';
import { toast } from 'sonner';

export default function AdvancedCreation({ api }) {
  const [projects, setProjects] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [colorSchemes, setColorSchemes] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState(null);
  const [showAdvancedCreateModal, setShowAdvancedCreateModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showImageGenerationModal, setShowImageGenerationModal] = useState(false);
  
  // Enhanced create project state
  const [newProject, setNewProject] = useState({
    name: '',
    description: '',
    project_type: 'landing_page',
    prompt: '',
    target_audience: '',
    industry: '',
    generate_images: true,
    include_forms: true,
    include_testimonials: true,
    include_cta: true,
    color_scheme: 'modern',
    visual_style: 'professional'
  });

  // Image generation state
  const [imageGeneration, setImageGeneration] = useState({
    prompt: '',
    style: 'photorealistic',
    size: '1024x1024',
    quality: 'hd'
  });

  // Generation state
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [generationStep, setGenerationStep] = useState('');

  useEffect(() => {
    initializeData();
  }, []);

  const initializeData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchProjects(),
        fetchTemplates(),
        fetchEmailTemplates(),
        fetchColorSchemes()
      ]);
    } catch (error) {
      console.error('Error initializing data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await api.get('/creation/projects');
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await api.get('/creation/templates/advanced?project_type=landing_page');
      if (response.data.success) {
        setTemplates(response.data.templates);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const fetchEmailTemplates = async () => {
    try {
      const response = await api.get('/creation/email/templates');
      if (response.data.success) {
        setEmailTemplates(response.data.templates);
      }
    } catch (error) {
      console.error('Error fetching email templates:', error);
    }
  };

  const fetchColorSchemes = async () => {
    try {
      const response = await api.get('/creation/color-schemes');
      if (response.data.success) {
        setColorSchemes(response.data.schemes);
      }
    } catch (error) {
      console.error('Error fetching color schemes:', error);
    }
  };

  const handleAdvancedGeneration = async () => {
    if (!newProject.name || !newProject.prompt) {
      toast.error('Preencha o nome e a descrição do projeto');
      return;
    }

    setIsGenerating(true);
    setGenerationProgress(0);
    setGenerationStep('Iniciando geração avançada...');

    try {
      const progressSteps = [
        { progress: 20, step: 'Analisando requisitos de design...' },
        { progress: 40, step: 'Gerando imagens personalizadas com DALL-E 3...' },
        { progress: 60, step: 'Selecionando imagens complementares de alta qualidade...' },
        { progress: 80, step: 'Aplicando templates visuais profissionais...' },
        { progress: 100, step: 'Finalizando projeto com elementos visuais...' }
      ];

      for (const step of progressSteps) {
        await new Promise(resolve => setTimeout(resolve, 1500));
        setGenerationProgress(step.progress);
        setGenerationStep(step.step);
      }

      const response = await api.post('/creation/advanced/generate', newProject);
      
      if (response.data) {
        toast.success('🎨 Projeto avançado gerado com sucesso!');
        await fetchProjects();
        setShowAdvancedCreateModal(false);
        setSelectedProject(response.data);
        setShowPreviewModal(true);
      }
    } catch (error) {
      console.error('Error generating project:', error);
      toast.error('Erro ao gerar projeto avançado');
    } finally {
      setIsGenerating(false);
      setGenerationProgress(0);
      setGenerationStep('');
    }
  };

  const handleVisualEmailGeneration = async () => {
    if (!newProject.name || !newProject.prompt) {
      toast.error('Preencha o nome e a descrição do email');
      return;
    }

    setIsGenerating(true);
    setGenerationProgress(0);
    setGenerationStep('Gerando email visual...');
    
    try {
      const progressSteps = [
        { progress: 25, step: 'Criando estrutura do email...' },
        { progress: 50, step: 'Gerando imagens para email...' },
        { progress: 75, step: 'Aplicando template responsivo...' },
        { progress: 100, step: 'Email visual pronto!' }
      ];

      for (const step of progressSteps) {
        await new Promise(resolve => setTimeout(resolve, 1000));
        setGenerationProgress(step.progress);
        setGenerationStep(step.step);
      }

      const emailRequest = {
        ...newProject,
        project_type: 'email_marketing'
      };
      
      const response = await api.post('/creation/email/generate-visual', emailRequest);
      
      if (response.data) {
        toast.success('📧 Email visual gerado com sucesso!');
        await fetchProjects();
        setShowAdvancedCreateModal(false);
        setSelectedProject(response.data);
        setShowPreviewModal(true);
      }
    } catch (error) {
      console.error('Error generating visual email:', error);
      toast.error('Erro ao gerar email visual');
    } finally {
      setIsGenerating(false);
      setGenerationProgress(0);
      setGenerationStep('');
    }
  };

  const handleGenerateImage = async () => {
    if (!imageGeneration.prompt) {
      toast.error('Digite uma descrição para a imagem');
      return;
    }

    setIsGenerating(true);
    
    try {
      const response = await api.post('/creation/images/generate', imageGeneration);
      
      if (response.data.success) {
        toast.success('🖼️ Imagem gerada com DALL-E 3!');
      }
    } catch (error) {
      console.error('Error generating image:', error);
      toast.error('Erro ao gerar imagem');
    } finally {
      setIsGenerating(false);
    }
  };

  const getProjectTypeIcon = (type) => {
    const icons = {
      landing_page: '🌐',
      email_marketing: '📧',
      presentation: '📊',
      logo: '🎨'
    };
    return icons[type] || '📄';
  };

  const getColorSchemePreview = (schemeName) => {
    const scheme = colorSchemes[schemeName];
    if (!scheme) return null;
    
    return (
      <div className="flex space-x-1">
        <div className="w-4 h-4 rounded-full" style={{ backgroundColor: scheme.primary }}></div>
        <div className="w-4 h-4 rounded-full" style={{ backgroundColor: scheme.secondary }}></div>
        <div className="w-4 h-4 rounded-full" style={{ backgroundColor: scheme.accent }}></div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 p-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Carregando sistema de criação avançada...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent mb-4">
            🎨 Criação com IA Avançada
          </h1>
          <p className="text-lg text-gray-600 mb-6">
            Crie landing pages e emails profissionais com DALL-E 3, templates avançados e designs personalizados
          </p>
          
          <div className="flex flex-wrap justify-center gap-4">
            <Button 
              onClick={() => {
                setNewProject({...newProject, project_type: 'landing_page'});
                setShowAdvancedCreateModal(true);
              }}
              className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-8 py-3 text-lg"
            >
              🌐 Criar Landing Page Avançada
            </Button>
            <Button 
              onClick={() => {
                setNewProject({...newProject, project_type: 'email_marketing'});
                setShowAdvancedCreateModal(true);
              }}
              className="bg-gradient-to-r from-green-600 to-teal-600 text-white px-8 py-3 text-lg"
            >
              📧 Criar Email Visual
            </Button>
            <Button 
              onClick={() => setShowImageGenerationModal(true)}
              variant="outline"
              className="border-2 border-purple-300 text-purple-600 hover:bg-purple-50 px-8 py-3 text-lg"
            >
              🖼️ Gerar Imagem DALL-E 3
            </Button>
          </div>
        </div>

        {/* Main Content */}
        <Tabs defaultValue="projects" className="w-full">
          <TabsList className="grid w-full grid-cols-4 mb-8">
            <TabsTrigger value="projects" className="text-base">📁 Meus Projetos</TabsTrigger>
            <TabsTrigger value="templates" className="text-base">🎨 Templates Landing Page</TabsTrigger>
            <TabsTrigger value="email-templates" className="text-base">📧 Templates Email</TabsTrigger>
            <TabsTrigger value="gallery" className="text-base">🖼️ Galeria</TabsTrigger>
          </TabsList>

          {/* Projects Tab */}
          <TabsContent value="projects">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {projects.map((project) => (
                <Card key={project.id} className="bg-white/80 backdrop-blur-sm border border-white/40 hover:shadow-lg transition-all duration-300">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <span className="text-2xl">{getProjectTypeIcon(project.project_type)}</span>
                        <div>
                          <CardTitle className="text-lg">{project.name}</CardTitle>
                          <Badge variant="secondary" className="text-xs">
                            {project.project_type.replace('_', ' ')}
                          </Badge>
                        </div>
                      </div>
                      {project.metadata?.generated_images && (
                        <Badge className="bg-purple-100 text-purple-800">
                          🎨 Com Imagens
                        </Badge>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-600 mb-4">{project.description}</p>
                    
                    {project.metadata?.color_scheme && (
                      <div className="mb-3">
                        <div className="flex items-center space-x-2">
                          <span className="text-xs text-gray-500">Esquema de cores:</span>
                          {getColorSchemePreview(project.metadata.color_scheme)}
                        </div>
                      </div>
                    )}
                    
                    <div className="flex space-x-2">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => {
                          setSelectedProject(project);
                          setShowPreviewModal(true);
                        }}
                        className="flex-1"
                      >
                        👁️ Preview
                      </Button>
                      {project.is_published && (
                        <Button size="sm" className="bg-green-600 text-white flex-1">
                          🚀 Publicado
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Landing Page Templates Tab */}
          <TabsContent value="templates">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {templates.map((template) => (
                <Card key={template.id} className="bg-white/80 backdrop-blur-sm border border-white/40 hover:shadow-lg transition-all duration-300">
                  <CardHeader>
                    <img 
                      src={template.preview} 
                      alt={template.name}
                      className="w-full h-40 object-cover rounded-lg mb-3"
                    />
                    <CardTitle className="flex items-center space-x-2">
                      <span>{template.name}</span>
                      {template.includes_images && (
                        <Badge className="bg-blue-100 text-blue-800">🖼️ Imagens</Badge>
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-600 mb-4">{template.description}</p>
                    <div className="flex flex-wrap gap-1 mb-4">
                      {template.sections?.map((section, index) => (
                        <Badge key={index} variant="outline" className="text-xs">
                          {section}
                        </Badge>
                      ))}
                    </div>
                    <Button 
                      className="w-full"
                      onClick={() => {
                        setNewProject({...newProject, template_id: template.id, color_scheme: template.color_scheme});
                        setShowAdvancedCreateModal(true);
                      }}
                    >
                      🎨 Usar Template
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Email Templates Tab */}
          <TabsContent value="email-templates">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {emailTemplates.map((template) => (
                <Card key={template.id} className="bg-white/80 backdrop-blur-sm border border-white/40 hover:shadow-lg transition-all duration-300">
                  <CardHeader>
                    <img 
                      src={template.preview} 
                      alt={template.name}
                      className="w-full h-48 object-cover rounded-lg mb-3"
                    />
                    <CardTitle className="flex items-center space-x-2">
                      <span>📧 {template.name}</span>
                      <Badge className="bg-green-100 text-green-800">{template.type}</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-600 mb-4">{template.description}</p>
                    <div className="flex flex-wrap gap-1 mb-4">
                      {template.features?.map((feature, index) => (
                        <Badge key={index} variant="outline" className="text-xs">
                          {feature}
                        </Badge>
                      ))}
                    </div>
                    <Button 
                      className="w-full bg-green-600 hover:bg-green-700"
                      onClick={() => {
                        setNewProject({
                          ...newProject, 
                          project_type: 'email_marketing',
                          template_id: template.id
                        });
                        setShowAdvancedCreateModal(true);
                      }}
                    >
                      📧 Usar Template Email
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Gallery Tab */}
          <TabsContent value="gallery">
            <div className="text-center py-20">
              <div className="text-6xl mb-4">🖼️</div>
              <h3 className="text-2xl font-semibold mb-4">Galeria de Imagens</h3>
              <p className="text-gray-600 mb-8">Visualize todas as imagens geradas com DALL-E 3</p>
              <Button onClick={() => setShowImageGenerationModal(true)}>
                🎨 Gerar Nova Imagem
              </Button>
            </div>
          </TabsContent>
        </Tabs>

        {/* Advanced Creation Modal */}
        <Dialog open={showAdvancedCreateModal} onOpenChange={setShowAdvancedCreateModal}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center space-x-2">
                <span>{getProjectTypeIcon(newProject.project_type)}</span>
                <span>Criação Avançada - {newProject.project_type === 'landing_page' ? 'Landing Page' : 'Email Marketing'}</span>
              </DialogTitle>
              <DialogDescription>
                Crie conteúdo profissional com IA, imagens personalizadas e templates avançados
              </DialogDescription>
            </DialogHeader>

            {isGenerating ? (
              <div className="py-8">
                <div className="text-center mb-6">
                  <div className="text-4xl mb-4">🎨</div>
                  <h3 className="text-lg font-semibold mb-2">Gerando seu projeto...</h3>
                  <p className="text-gray-600">{generationStep}</p>
                </div>
                <Progress value={generationProgress} className="w-full" />
                <div className="text-center mt-4">
                  <p className="text-sm text-gray-500">{generationProgress}% concluído</p>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <Label>Nome do Projeto</Label>
                    <Input
                      value={newProject.name}
                      onChange={(e) => setNewProject({...newProject, name: e.target.value})}
                      placeholder="Ex: Landing Page Curso de Marketing"
                    />
                  </div>

                  <div>
                    <Label>Descrição/Prompt</Label>
                    <Textarea
                      value={newProject.prompt}
                      onChange={(e) => setNewProject({...newProject, prompt: e.target.value})}
                      placeholder="Descreva detalhadamente o que você deseja criar..."
                      rows={4}
                    />
                  </div>

                  <div>
                    <Label>Público-alvo</Label>
                    <Input
                      value={newProject.target_audience}
                      onChange={(e) => setNewProject({...newProject, target_audience: e.target.value})}
                      placeholder="Ex: Empreendedores, Estudantes"
                    />
                  </div>

                  <div>
                    <Label>Indústria</Label>
                    <Input
                      value={newProject.industry}
                      onChange={(e) => setNewProject({...newProject, industry: e.target.value})}
                      placeholder="Ex: Tecnologia, Educação"
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <Label>Esquema de Cores</Label>
                    <Select value={newProject.color_scheme} onValueChange={(value) => setNewProject({...newProject, color_scheme: value})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="modern">🔵 Moderno (Azul)</SelectItem>
                        <SelectItem value="corporate">⚫ Corporativo (Cinza)</SelectItem>
                        <SelectItem value="vibrant">🔴 Vibrante (Vermelho)</SelectItem>
                        <SelectItem value="minimal">⚪ Minimal (Preto/Branco)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Estilo Visual</Label>
                    <Select value={newProject.visual_style} onValueChange={(value) => setNewProject({...newProject, visual_style: value})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="professional">👔 Profissional</SelectItem>
                        <SelectItem value="creative">🎨 Criativo</SelectItem>
                        <SelectItem value="minimal">⚪ Minimalista</SelectItem>
                        <SelectItem value="bold">💪 Impactante</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-3">
                    <Label>Funcionalidades</Label>
                    
                    <div className="flex items-center space-x-2">
                      <Switch
                        checked={newProject.generate_images}
                        onCheckedChange={(checked) => setNewProject({...newProject, generate_images: checked})}
                      />
                      <Label className="text-sm">🎨 Gerar imagens com DALL-E 3</Label>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Switch
                        checked={newProject.include_forms}
                        onCheckedChange={(checked) => setNewProject({...newProject, include_forms: checked})}
                      />
                      <Label className="text-sm">📝 Incluir formulários</Label>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Switch
                        checked={newProject.include_testimonials}
                        onCheckedChange={(checked) => setNewProject({...newProject, include_testimonials: checked})}
                      />
                      <Label className="text-sm">💬 Incluir depoimentos</Label>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Switch
                        checked={newProject.include_cta}
                        onCheckedChange={(checked) => setNewProject({...newProject, include_cta: checked})}
                      />
                      <Label className="text-sm">🎯 Call-to-Actions</Label>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <DialogFooter>
              {!isGenerating && (
                <>
                  <Button variant="outline" onClick={() => setShowAdvancedCreateModal(false)}>
                    Cancelar
                  </Button>
                  {newProject.project_type === 'landing_page' ? (
                    <Button onClick={handleAdvancedGeneration} className="bg-gradient-to-r from-purple-600 to-blue-600">
                      🌐 Gerar Landing Page Avançada
                    </Button>
                  ) : (
                    <Button onClick={handleVisualEmailGeneration} className="bg-gradient-to-r from-green-600 to-teal-600">
                      📧 Gerar Email Visual
                    </Button>
                  )}
                </>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Image Generation Modal */}
        <Dialog open={showImageGenerationModal} onOpenChange={setShowImageGenerationModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>🖼️ Gerar Imagem com DALL-E 3</DialogTitle>
              <DialogDescription>
                Crie imagens únicas e profissionais com inteligência artificial
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div>
                <Label>Descrição da Imagem</Label>
                <Textarea
                  value={imageGeneration.prompt}
                  onChange={(e) => setImageGeneration({...imageGeneration, prompt: e.target.value})}
                  placeholder="Ex: Uma landing page moderna para uma empresa de tecnologia..."
                  rows={3}
                />
              </div>

              <div>
                <Label>Estilo</Label>
                <Select value={imageGeneration.style} onValueChange={(value) => setImageGeneration({...imageGeneration, style: value})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="photorealistic">📷 Fotorrealista</SelectItem>
                    <SelectItem value="artistic">🎨 Artístico</SelectItem>
                    <SelectItem value="illustration">🖌️ Ilustração</SelectItem>
                    <SelectItem value="logo">🏷️ Logo/Marca</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Tamanho</Label>
                <Select value={imageGeneration.size} onValueChange={(value) => setImageGeneration({...imageGeneration, size: value})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1024x1024">📱 Quadrado (1024x1024)</SelectItem>
                    <SelectItem value="1024x1792">📄 Retrato (1024x1792)</SelectItem>
                    <SelectItem value="1792x1024">🖥️ Paisagem (1792x1024)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowImageGenerationModal(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleGenerateImage} 
                disabled={isGenerating}
                className="bg-gradient-to-r from-pink-600 to-purple-600"
              >
                {isGenerating ? '🔄 Gerando...' : '🎨 Gerar com DALL-E 3'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Preview Modal */}
        <Dialog open={showPreviewModal} onOpenChange={setShowPreviewModal}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center space-x-2">
                <span>{selectedProject && getProjectTypeIcon(selectedProject.project_type)}</span>
                <span>Preview: {selectedProject?.name}</span>
                {selectedProject?.metadata?.generated_images && (
                  <Badge className="bg-purple-100 text-purple-800">
                    🎨 Com Imagens IA
                  </Badge>
                )}
              </DialogTitle>
            </DialogHeader>

            {selectedProject && (
              <div className="space-y-4">
                <div className="bg-gray-100 p-4 rounded-lg max-h-96 overflow-y-auto">
                  <iframe 
                    srcDoc={selectedProject.generated_html || selectedProject.generated_content} 
                    className="w-full h-96 border-0 rounded"
                    title="Preview"
                  />
                </div>
                
                {selectedProject.metadata?.images && (
                  <div>
                    <h4 className="font-semibold mb-2">🖼️ Imagens Geradas:</h4>
                    <div className="grid grid-cols-2 gap-2">
                      {selectedProject.metadata.images.map((image, index) => (
                        <img 
                          key={index}
                          src={image.url}
                          alt={image.alt}
                          className="w-full h-32 object-cover rounded"
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowPreviewModal(false)}>
                Fechar
              </Button>
              {selectedProject && !selectedProject.is_published && (
                <Button className="bg-green-600 hover:bg-green-700">
                  🚀 Publicar Projeto
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}